import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import instantsearch from 'instantsearch.js';
import { configure, hits, stats } from 'instantsearch.js/es/widgets';
import connectSearchBox from 'instantsearch.js/es/connectors/search-box/connectSearchBox';
import { de } from '../search/translations';

const searchBoxMarkup = widgetParams => {
    const inputId = `quick-search-input-${Math.random().toString(36).slice(2, 11)}`;

    return `
        <form class="quick-search__form" role="search" novalidate>
            <div class="ais-SearchBox-wrapper">
                <label class="visually-hidden" for="${inputId}">
                    ${widgetParams.inputAriaLabel || 'Suche'}
                </label>

                <input
                    type="search"
                    id="${inputId}"
                    class="quick-search__input form-input"
                    placeholder="${widgetParams.placeholderText || ''}"
                    autocomplete="off"
                    autocorrect="off"
                    autocapitalize="none"
                    spellcheck="false"
                    maxlength="512"
                    aria-label="${widgetParams.inputAriaLabel || 'Suche'}"
                />

                <button
                    type="submit"
                    class="ais-SearchBox-submit"
                    title="${widgetParams.submitAriaLabel || 'Suchen'}"
                    aria-label="${widgetParams.submitAriaLabel || 'Suchen'}"
                >
                    <svg class="ais-SearchBox-submitIcon" width="10" height="10" viewBox="0 0 40 40" aria-hidden="true">
                        <path d="M26.804 29.01c-2.832 2.34-6.465 3.746-10.426 3.746C7.333 32.756 0 25.424 0 16.378 0 7.333 7.333 0 16.378 0c9.046 0 16.378 7.333 16.378 16.378 0 3.96-1.406 7.594-3.746 10.426l10.534 10.534c.607.607.61 1.59-.004 2.202-.61.61-1.597.61-2.202.004L26.804 29.01zm-10.426.627c7.323 0 13.26-5.936 13.26-13.26 0-7.32-5.937-13.257-13.26-13.257C9.056 3.12 3.12 9.056 3.12 16.378c0 7.323 5.936 13.26 13.258 13.26z"/>
                    </svg>
                </button>

                <button
                    type="reset"
                    class="ais-SearchBox-reset"
                    title="${widgetParams.resetAriaLabel || 'Suche zurücksetzen'}"
                    aria-label="${widgetParams.resetAriaLabel || 'Suche zurücksetzen'}"
                    hidden
                >
                    <svg class="ais-SearchBox-resetIcon" viewBox="0 0 20 20" width="10" height="10" aria-hidden="true">
                        <path d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z"/>
                    </svg>
                </button>

                <span class="ais-SearchBox-loadingIndicator" hidden>
                    <svg aria-label="${widgetParams.loadingAriaLabel || 'Suchergebnisse werden geladen'}" class="ais-SearchBox-loadingIcon" width="16" height="16" viewBox="0 0 38 38" stroke="#444" aria-hidden="true">
                        <g fill="none" fill-rule="evenodd">
                            <g transform="translate(1 1)" stroke-width="2">
                                <circle stroke-opacity=".5" cx="18" cy="18" r="18"></circle>
                                <path d="M36 18c0-9.94-8.06-18-18-18">
                                    <animateTransform attributeName="transform" type="rotate" from="0 18 18" to="360 18 18" dur="1s" repeatCount="indefinite"></animateTransform>
                                </path>
                            </g>
                        </g>
                    </svg>
                </span>
            </div>
        </form>
    `;
};

const searchBoxElements = container => ({
    form: container.querySelector('.quick-search__form'),
    input: container.querySelector('.quick-search__input'),
    resetButton: container.querySelector('.ais-SearchBox-reset'),
    loadingIndicator: container.querySelector('.ais-SearchBox-loadingIndicator'),
    submitButton: container.querySelector('.ais-SearchBox-submit'),
});

const quickSearchBoxConnector = quickSearch => {
    const renderSearchBox = (renderOptions, isFirstRender) => {
        const { query, refine, isSearchStalled, widgetParams } = renderOptions;
        const container = widgetParams.container;

        if (!container) return;

        if (isFirstRender) {
            container.innerHTML = searchBoxMarkup(widgetParams);

            const { form, input, resetButton, submitButton } = searchBoxElements(container);

            form?.addEventListener('submit', e => {
                e.preventDefault();
                const value = input?.value?.trim() || '';

                if (widgetParams.action) {
                    window.location.href = quickSearch.action(value);
                    return;
                }

                refine(value);
            });

            input?.addEventListener('input', e => {
                refine(e.target.value);
                if (resetButton) resetButton.hidden = !e.target.value;
                quickSearch.updateResultsVisibility();
            });

            input?.addEventListener('focus', () => {
                quickSearch.updateResultsVisibility();
            });

            resetButton?.addEventListener('click', () => {
                if (!input) return;
                input.value = '';
                refine('');
                resetButton.hidden = true;
                quickSearch.updateResultsVisibility();
            });

            if (submitButton && widgetParams.submitButtonTitle) {
                submitButton.setAttribute('title', widgetParams.submitButtonTitle);
            }
        }

        const { input, resetButton, loadingIndicator } = searchBoxElements(container);

        if (input && input !== document.activeElement && input.value !== query) {
            input.value = query;
        }

        if (resetButton) resetButton.hidden = !query;
        if (loadingIndicator) loadingIndicator.hidden = !isSearchStalled;

        quickSearch.updateResultsVisibility();
    };

    return connectSearchBox(renderSearchBox);
};

export class QuickSearch {
    static defaults = {
        resultsCount: 5,
        action: null,
        placeholderText: 'Suchen …',
        submitButtonText: 'Suchen',
        submitButtonTitle: 'Suchanfrage absenden',
        statsTemplate: ({ totalResults }) => {
            const resultText = totalResults === 1 ? 'Ergebnis' : 'Ergebnisse';
            return `Alle ${totalResults} ${resultText} ansehen`;
        },
        hitTemplate: hit => {
            const typeName = hit.type_handle ? de[`type.${hit.type_handle}`] : '';
            return `
                <a href="${hit.url}" class="quick-search__result-item">
                    <div class="quick-search__result-content">
                        <span class="quick-search__result-title">${hit.title}</span>
                        ${typeName ? `<span class="quick-search__result-type">${typeName}</span>` : ''}
                    </div>
                    <svg class="quick-search__result-icon" width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.75 6.625L8.25 11.875C7.9375 12.1562 7.46875 12.1562 7.1875 11.8438C6.90625 11.5312 6.90625 11.0625 7.21875 10.7812L11.375 6.8125H0.75C0.3125 6.8125 0 6.5 0 6.0625C0 5.65625 0.3125 5.3125 0.75 5.3125H11.375L7.21875 1.375C6.90625 1.09375 6.90625 0.59375 7.1875 0.3125C7.46875 0 7.96875 0 8.25 0.28125L13.75 5.53125C13.9062 5.6875 14 5.875 14 6.0625C14 6.28125 13.9062 6.46875 13.75 6.625Z" fill="#057DAA"/>
                    </svg>
                </a>
            `;
        },
        submitAriaLabel: 'Suchen',
        inputAriaLabel: 'Suche',
        resetAriaLabel: 'Suche zurücksetzen',
        loadingAriaLabel: 'Suchergebnisse werden geladen',
    };

    static isMeiliSearchConfigValid() {
        if (!window.MeiliSearchConfig) return false;
        const { url, indexName } = window.MeiliSearchConfig;
        return Boolean(url && indexName);
    }

    constructor(root, options = {}) {
        if (!QuickSearch.isMeiliSearchConfigValid()) return;

        this.root = root;
        this.config = {
            ...QuickSearch.defaults,
            ...this.parseDataAttributes(),
            ...options,
        };

        this.containers = this.findContainers();
        if (!this.hasRequiredContainers()) return;

        this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
        this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);

        this.initializeInstantSearch();
        this.attachGlobalListeners();
    }

    parseDataAttributes() {
        const { dataset } = this.root;

        return {
            resultsCount: dataset.resultsCount ? parseInt(dataset.resultsCount, 10) : undefined,
            action: dataset.action || undefined,
            placeholderText: dataset.placeholderText || undefined,
            submitButtonText: dataset.submitButtonText || undefined,
        };
    }

    findContainers() {
        return {
            searchBox: this.root.querySelector('[data-widget="quickSearchBox"]'),
            hits: this.root.querySelector('[data-widget="quickSearchHits"]'),
            stats: this.root.querySelector('[data-widget="quickSearchStats"]'),
            results: this.root.querySelector('[data-widget="quickSearchResults"]'),
        };
    }

    hasRequiredContainers() {
        return Boolean(this.containers.searchBox && this.containers.results);
    }

    initializeInstantSearch() {
        const { url, apiKey, indexName } = window.MeiliSearchConfig;
        const { searchClient } = instantMeiliSearch(url, apiKey || '');

        this.search = instantsearch({ searchClient, indexName });

        const searchBoxWidget = quickSearchBoxConnector(this);

        this.search.addWidgets([
            searchBoxWidget({
                container: this.containers.searchBox,
                placeholderText: this.config.placeholderText,
                submitAriaLabel: this.config.submitAriaLabel,
                inputAriaLabel: this.config.inputAriaLabel,
                resetAriaLabel: this.config.resetAriaLabel,
                loadingAriaLabel: this.config.loadingAriaLabel,
                submitButtonTitle: this.config.submitButtonTitle,
                action: this.config.action,
            }),
            configure({ hitsPerPage: this.config.resultsCount }),
        ]);

        if (this.containers.hits) {
            this.search.addWidgets([
                hits({
                    container: this.containers.hits,
                    templates: {
                        item: this.config.hitTemplate,
                        empty: results => null,
                    },
                }),
            ]);
        }

        if (this.containers.stats && this.config.action) {
            this.search.addWidgets([
                stats({
                    container: this.containers.stats,
                    templates: {
                        text: (statsData, { html }) => {
                            const { nbHits: totalResults, query } = statsData;
                            const limit = this.config.resultsCount;

                            if (query && totalResults === 0) {
                                return html`<p class="quick-search__no-results">
                                    Keine Ergebnisse gefunden
                                </p>`;
                            }

                            if (totalResults > limit) {
                                const url = this.action(query);
                                const label = this.config.statsTemplate({ totalResults });
                                return html`<a
                                    href="${url}"
                                    class="quick-search__results-button button button--small"
                                    >${label}</a
                                >`;
                            }

                            return '';
                        },
                    },
                }),
            ]);
        }

        this.search.start();
    }

    attachGlobalListeners() {
        document.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
        document.addEventListener('keydown', this.handleDocumentKeyDown, true);
    }

    handleDocumentPointerDown(event) {
        const searchBoxContainer = this.containers.searchBox;
        const resultsContainer = this.containers.results;
        const target = event.target;

        const clickedInsideWidget =
            (searchBoxContainer && searchBoxContainer.contains(target)) ||
            (resultsContainer && resultsContainer.contains(target));

        if (!clickedInsideWidget) {
            this.containers.results?.classList.remove('quick-search__results--visible');
        }
    }

    handleDocumentKeyDown(event) {
        if (event.key === 'Escape') {
            this.containers.results?.classList.remove('quick-search__results--visible');
            return;
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

        const active = document.activeElement;

        const focusInsideWidget =
            (this.containers.searchBox && this.containers.searchBox.contains(active)) ||
            (this.containers.results && this.containers.results.contains(active));

        if (!focusInsideWidget) return;

        event.preventDefault();
        event.stopPropagation();

        const cycle = this.focusableItems();
        if (cycle.length === 0) return;

        const input = this.containers.searchBox?.querySelector('.quick-search__input');
        const isOnInput = input && active === input;
        const isOnSearchBoxControl =
            isOnInput || (this.containers.searchBox && this.containers.searchBox.contains(active));

        if (isOnSearchBoxControl) {
            if (event.key === 'ArrowDown') {
                cycle[0].focus();
            } else {
                cycle[cycle.length - 1].focus();
            }
            return;
        }

        const currentIndex = cycle.indexOf(active);
        if (currentIndex === -1) {
            if (event.key === 'ArrowDown') {
                cycle[0].focus();
            } else {
                cycle[cycle.length - 1].focus();
            }
            return;
        }

        if (event.key === 'ArrowDown') {
            const nextIndex = (currentIndex + 1) % cycle.length;
            cycle[nextIndex].focus();
            return;
        }

        const prevIndex = (currentIndex - 1 + cycle.length) % cycle.length;
        cycle[prevIndex].focus();
    }

    focusableItems() {
        const resultItems = Array.from(
            this.containers.results?.querySelectorAll('.quick-search__result-item') || [],
        );
        const resultsButton = this.containers.results?.querySelector(
            '.quick-search__results-button',
        );

        if (resultsButton) {
            return [...resultItems, resultsButton];
        }

        return resultItems;
    }

    action(query) {
        if (!this.config.action) return '#';

        const url = new URL(this.config.action, window.location.origin);
        if (query) url.searchParams.set('q', query);
        return url.toString();
    }

    updateResultsVisibility() {
        const input = this.containers.searchBox?.querySelector('.quick-search__input');
        const value = input?.value?.trim() || '';
        const hasFocus = document.activeElement === input;

        const shouldShow = hasFocus && value.length > 0;
        this.containers.results?.classList.toggle('quick-search__results--visible', shouldShow);
    }

    destroy() {
        this.search?.dispose();
        document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
        document.removeEventListener('keydown', this.handleDocumentKeyDown, true);
    }
}

export const initializeQuickSearch = () => {
    document.querySelectorAll('[data-quick-search]').forEach(root => {
        new QuickSearch(root);
    });
};

initializeQuickSearch();
